import { z } from "zod";

const ErrorObjectSchema = z.record(z.string(), z.unknown());
const EMAIL_TAKEN_MESSAGE = "email has already been taken";
// Fullscript's documented 403 body: {"error": "Must have `catalog:read` scope enabled."}
const MISSING_SCOPE_MESSAGE = /^Must have `([a-z0-9_:]+)` scope enabled\.$/;
const ScopeErrorBodySchema = z.object({ error: z.string() });

/** Loggable shape of a Fullscript error body: key names and flags only, never the messages. */
export interface FullscriptErrorSummary {
  /** Top-level keys of a JSON error body, e.g. ["errors"]. */
  errorKeys: string[];
  /** Field names under `errors` when Fullscript keys its errors by field. */
  errorFields: string[];
  messageCount: number;
  /** Fullscript refused the create because a patient with this email exists. */
  isEmailTaken: boolean;
}

function parseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

function toStrings(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item) => typeof item === "string") : [];
}

/**
 * Messages of the `error` property: a string array, a stringified array, or a plain string.
 * A string that parses as JSON but not as an array gives no messages, the same as the original
 * duplicate check.
 */
function readErrorProperty(value: unknown): string[] {
  if (typeof value !== "string") return toStrings(value);
  const nested = parseJson(value);
  if (nested === undefined) return [value];
  return toStrings(nested);
}

function hasEmailTakenMessage(messages: string[]): boolean {
  // Fullscript English copy; a match on text is the only signal the body gives
  return messages.some((message) => message.toLowerCase().includes(EMAIL_TAKEN_MESSAGE));
}

/**
 * Reduces a Fullscript error body to key names and PHI-free flags. Fullscript error messages can
 * echo a submitted value, such as a date of birth or a phone number, so the raw body must never
 * reach a log.
 *
 * `isEmailTaken` reads the bare array, the `error` property, and only the `email` key of the
 * `errors` map. Another field's message, such as "Guardian email has already been taken", must
 * not link this member to an existing Fullscript patient.
 */
export function summarizeFullscriptErrorBody(errorText: string): FullscriptErrorSummary {
  const body = parseJson(errorText);
  const object = Array.isArray(body) ? undefined : ErrorObjectSchema.safeParse(body).data;
  const fieldErrors = ErrorObjectSchema.safeParse(object?.errors).data;

  const topLevelMessages = object ? readErrorProperty(object.error) : toStrings(body);
  const emailFieldMessages = toStrings(fieldErrors?.email);
  const allFieldMessages = Object.values(fieldErrors ?? {}).flatMap(toStrings);
  return {
    errorKeys: Object.keys(object ?? {}),
    errorFields: Object.keys(fieldErrors ?? {}),
    messageCount: topLevelMessages.length + allFieldMessages.length,
    isEmailTaken: hasEmailTakenMessage([...topLevelMessages, ...emailFieldMessages]),
  };
}

/**
 * The OAuth scope that Fullscript's documented 403 body names, e.g. `catalog:read`. Undefined for
 * any other body. Only the scope name leaves this function: it is safe to log, and the rest of a
 * body is not. Takes the raw text or the parsed JSON.
 */
export function readMissingScope(body: unknown): string | undefined {
  const parsed = ScopeErrorBodySchema.safeParse(typeof body === "string" ? parseJson(body) : body);
  if (!parsed.success) return undefined;
  // the body has no machine code for a missing scope; the documented message is the only signal
  return MISSING_SCOPE_MESSAGE.exec(parsed.data.error)?.[1];
}
